import { CalendarDays } from 'lucide-react';
import { todayInTz } from '@trestly/shared';
import { Chip, chipClass } from './Chip';
import { formatIsoDate, shiftIsoDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '../i18n';

interface DateChipsProps {
  value: string;
  onChange: (iso: string) => void;
  /** Мельче и без подписи «Другая дата» — в листе с клавиатурой. */
  compact?: boolean;
}

/**
 * Дата траты тремя таблетками. В 95% случаев это «сегодня»; календарь нужен
 * редко, поэтому он спрятан за третьей таблеткой, а не показан всегда.
 */
export function DateChips({ value, onChange, compact = false }: DateChipsProps): JSX.Element {
  const today = todayInTz();
  const yesterday = shiftIsoDate(today, -1);
  const isCustom = value !== today && value !== yesterday;
  const size = compact ? 'min-h-8 px-3 text-[13px]' : undefined;

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('components.txDate')}>
      <Chip active={value === today} onClick={() => onChange(today)} className={size}>
        {t('components.today')}
      </Chip>
      <Chip active={value === yesterday} onClick={() => onChange(yesterday)} className={size}>
        {t('components.yesterday')}
      </Chip>

      {/* Инпут лежит поверх таблетки: тап открывает системный календарь. */}
      <label className={chipClass(isCustom, cn('relative overflow-hidden', size))}>
        <CalendarDays className="size-4" strokeWidth={1.75} aria-hidden />
        {isCustom ? formatIsoDate(value) : compact ? t('components.date') : t('components.otherDate')}
        <input
          type="date"
          value={value}
          aria-label={t('components.otherDate')}
          className="absolute inset-0 cursor-pointer opacity-0"
          onChange={(event) => {
            if (event.target.value) onChange(event.target.value);
          }}
        />
      </label>
    </div>
  );
}
